import { 
  Shield, 
  ExternalLink, 
  Lock,
  HelpCircle,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

const portalFaqs = [
  {
    question: "How do I access the secure portal?",
    answer: "Accepted clients receive an email invitation with a secure link to MyTAXPrepOffice. If you haven't received an invitation, contact us and we'll send one right away."
  },
  {
    question: "I forgot my password. How do I reset it?",
    answer: "Click the 'Forgot Password' link on the login page. You'll receive an email with instructions to reset your password."
  },
  {
    question: "What can I do in the portal?",
    answer: "Securely upload requested documents, review requests, complete applicable signatures, and access your client communications and filed returns."
  },
  {
    question: "Is the portal secure?",
    answer: "Yes. MyTAXPrepOffice uses bank-level encryption to protect your data. Your documents are transmitted and stored securely. Never email sensitive documents—always use the secure portal."
  }
];

export default function Portal() {
  const settings = useSettings();
  const PORTAL_URL = settings.portal_url;
  return (
    <div data-testid="portal-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Client Portal
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Secure Client Portal
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300">
              Existing clients can securely upload requested documents, review requests, complete applicable signatures, and access client communications through MyTAXPrepOffice.
            </p>
          </div>
        </div>
      </section>

      {/* Portal Options */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-lg mx-auto">
            {/* Primary Portal */}
            <Card className="bg-white border-2 border-bronze shadow-lg">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 rounded-full bg-bronze/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-bronze" />
                </div>
                <CardTitle className="font-heading text-2xl text-navy">
                  MyTAXPrepOffice
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="font-body text-gray-600 mb-6">
                  Securely upload requested documents, review requests, complete applicable signatures, and communicate with our team.
                </p>
                <ul className="text-left space-y-2 mb-8">
                  <li className="flex items-center gap-2 font-body text-sm text-gray-700">
                    <Shield className="w-4 h-4 text-bronze flex-shrink-0" />
                    Secure document upload
                  </li>
                  <li className="flex items-center gap-2 font-body text-sm text-gray-700">
                    <Shield className="w-4 h-4 text-bronze flex-shrink-0" />
                    E-signature for tax returns
                  </li>
                  <li className="flex items-center gap-2 font-body text-sm text-gray-700">
                    <Shield className="w-4 h-4 text-bronze flex-shrink-0" />
                    Direct messaging with our team
                  </li>
                  <li className="flex items-center gap-2 font-body text-sm text-gray-700">
                    <Shield className="w-4 h-4 text-bronze flex-shrink-0" />
                    Access your filed returns
                  </li>
                </ul>
                <Button
                  asChild
                  className="w-full bg-bronze hover:bg-bronze/90 text-navy rounded-full py-6 text-lg font-medium"
                  data-testid="portal-primary-btn"
                >
                  <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                    Secure Client Login
                    <ExternalLink className="ml-2 w-5 h-5" />
                  </a>
                </Button>
                <p className="font-body text-sm text-gray-500 mt-4">
                  New here? <Link to="/start" className="text-bronze underline">Start Your Return</Link> to get your secure portal instructions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Security Note */}
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-red-800 mb-2">
                  Security Reminder
                </h3>
                <p className="font-body text-red-700">
                  For your protection, do not email or text Social Security numbers, identification documents, W-2s, 1099s, bank information, tax returns, or other sensitive records. Use the secure client portal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portal FAQ */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-bronze" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-navy mb-4">
              Portal Help
            </h2>
            <p className="font-body text-gray-600">
              Common questions about using the client portals.
            </p>
          </div>

          <div className="space-y-4">
            {portalFaqs.map((faq, index) => (
              <Card key={index} className="bg-white border-0 shadow-sm" data-testid={`portal-faq-${index}`}>
                <CardContent className="p-6">
                  <h3 className="font-body font-semibold text-navy mb-2">
                    {faq.question}
                  </h3>
                  <p className="font-body text-gray-600">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact for Help */}
          <Card className="bg-navy text-white mt-8">
            <CardContent className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-bronze mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">
                Need More Help?
              </h3>
              <p className="font-body text-gray-300 mb-6">
                If you're having trouble accessing the portal or have other questions, reach out to us.
              </p>
              <Button
                asChild
                className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-4 font-medium"
                data-testid="portal-contact-btn"
              >
                <Link to="/contact">
                  Contact Support
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
